import Delete from "@mui/icons-material/Delete";
import { type GridRenderCellParams, type GridRowSelectionModel } from "@mui/x-data-grid-pro";
import axios, { type AxiosError } from "axios";
import { type ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import apiRoutes from "../../../constants/api-routes";
import messageLevels from "../../../constants/message-levels";
import messages from "../../../constants/messages";
import {
	emptyGridRowSelectionModel, gridRowSelectionIdsArray,
	gridRowSelectionSize, resolveGridSelectedRowIds
} from "../../../helpers/dataGridRowSelectionModel";
import useAuthHeader from "../../../hooks/useAuthHeader";
import useCoAppNavigation from "../../../hooks/useCoAppNavigation";
import useDatagridSorting from "../../../hooks/useDatagridSorting";
import useToastAlert from "../../../hooks/useToastAlert";
import { selectOrganizationSsoAndScim } from "../../../redux/userSlice";
import CoAppDatagridHeader from "../../global/components/datagrid/CoAppDatagridHeader";
import CoAppStandardDataGrid from "../../global/components/datagrid/CoAppStandardDataGrid";
import useCoAppDataGridFilters from "../../global/components/datagrid/useCoAppDataGridFilters";
import CoAppCreateUserModal from "../../global/components/modals/CoAppCreateUserModal";
import CoAppDestructiveConfirmationModal from "../../global/components/modals/CoAppDestructiveConfirmationModal";
import SettingsDataGridSelectionRow, { destructiveIconSx } from "../SettingsDataGridSelectionRow";
import { getUserManagementColumns, type UserRow } from "./columns/user-management-columns";


interface Role {
	id?: string;
	name: string;
	[key: string]: unknown;
}

interface OrganizationUser extends UserRow {
	id: string;
	email: string;
	firstname?: string;
	lastname?: string;
	roles: string[];
	groups: string[];
	active: boolean;
	isCoAppSuperAdmin?: boolean;
	descopeUserId?: string;
	lastHeartbeat?: string | null;
	createdAt?: string | null;
	workstationId?: string | null;
	user?: {
		firstname?: string;
		lastname?: string;
		email?: string;
	};
	[key: string]: unknown;
}

interface UserCreateFormState {
	email: string;
	firstName: string;
	lastName: string;
	roles: Role[];
	emailError: string;
	firstNameError: string;
	lastNameError: string;
	roleError: string;
}

const userCreateInitialState: UserCreateFormState = {
	email: "",
	firstName: "",
	lastName: "",
	roles: [],
	emailError: "",
	firstNameError: "",
	lastNameError: "",
	roleError: "",
};

export default function UserManagement() {
	const navigate = useNavigate();
	const authHeader = useAuthHeader();
	const { viewUserPageFromUserManagement } = useCoAppNavigation();
	const { sortModel, handleSortModelChange, handleInitializeSortModel } = useDatagridSorting("users");
	const { handleToastAlert } = useToastAlert();

	const organizationSsoStatus = useSelector(selectOrganizationSsoAndScim);
	const [organizationUsers, setOrganizationUsers] = useState<OrganizationUser[]>([]);
	const [userBeingDeleted, setUserBeingDeleted] = useState<Partial<OrganizationUser>>({});
	const [userCreationDialogOpen, setUserCreationDialogOpen] = useState(false);
	const [userCreateData, setUserCreateData] = useState<UserCreateFormState>(userCreateInitialState);
	const [availableRoles, setAvailableRoles] = useState<Role[]>([]);
	const [userDeletionDialogOpen, setUserDeletionDialogOpen] = useState(false);
	const [userSeed, setUserSeed] = useState(1);
	const [selectedUsers, setSelectedUsers] = useState<GridRowSelectionModel>(() => emptyGridRowSelectionModel());
	const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
	const [loading, setLoading] = useState(true);
	const {
		apiRef,
		gridFilterModel,
		onFilterModelChange,
		quickFilterSearchProps,
		visibleRowCount,
		filterControl,
		filterButtonEl,
	} = useCoAppDataGridFilters(organizationUsers, loading);

	const initUserManagement = useCallback(() => {
		axios.get<OrganizationUser[]>(apiRoutes.getUsers, { headers: authHeader })
			.then((res) => {
				const normalizedUsers: OrganizationUser[] = res.data.map((user) => {
					user["user"] = {
						firstname: user.firstname,
						lastname: user.lastname,
						email: user.email
					};
					return user;
				});
				setOrganizationUsers(normalizedUsers);
				setLoading(false);
			}).catch((err) => {
				console.log(err);
			});
	}, [authHeader]);

	const handleCloseUserDeletionDialog = useCallback(() => {
		setUserDeletionDialogOpen(false);
		setUserBeingDeleted({});
	}, []);

	const handleOpenUserDeletionDialog = useCallback((rowObject: { params: GridRenderCellParams<UserRow> }) => {
		const userInfo = rowObject.params.row as OrganizationUser;
		setUserBeingDeleted(userInfo);
		setUserDeletionDialogOpen(true);
	}, []);

	const handleUserStatusToggle = useCallback((id: string, state: boolean | "false" | "true") => {
		const userJSON = {
			active: state
		};
		axios.put(apiRoutes.setUserActive(id), userJSON, { headers: authHeader })
			.then(() => {
				initUserManagement();
			})
			.catch((error) => {
				console.error(error);
				handleToastAlert(messageLevels.ERROR, messages.USER_TOGGLE_ERROR_MSG);
			});
	}, [authHeader, initUserManagement, handleToastAlert]);

	const handleWorkstationClick = useCallback((workstationId?: string | null) => {
		if (!workstationId) {
			return;
		}
		navigate(`/settings/workstations?workstationId=${workstationId}`);
	}, [navigate]);

	const viewUserPageFromGrid = useCallback((params: GridRenderCellParams<UserRow>) => {
		const row = params.row as OrganizationUser;
		viewUserPageFromUserManagement({
			id: params.id == null ? undefined : String(params.id),
			row: {
				id: row.id,
				firstname: row.firstname ?? row.user?.firstname,
				lastname: row.lastname ?? row.user?.lastname,
				email: row.email ?? row.user?.email ?? "",
			},
		});
	}, [viewUserPageFromUserManagement]);

	// Stable column defs — new arrays on each render make MUI X discard autosized widths.
	const columns = useMemo(
		() => getUserManagementColumns(
			viewUserPageFromGrid,
			handleOpenUserDeletionDialog,
			handleUserStatusToggle,
			organizationSsoStatus ?? false,
			handleWorkstationClick
		),
		[organizationSsoStatus]
	);

	const handleUserCreateDataChange = (key: keyof UserCreateFormState | "roles", value: string | string[]) => {
		const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
		switch (key) {
			case "email":
				if (typeof value !== "string") {
					return;
				}
				if (!emailRegex.test(value)) {
					setUserCreateData({ ...userCreateData, email: value, emailError: value.length === 0 ? "" : "Invalid email" });
					return;
				} else {
					setUserCreateData({ ...userCreateData, email: value, emailError: "" });
				}
				break;
			case "firstName":
				if (typeof value !== "string") {
					return;
				}
				setUserCreateData({ ...userCreateData, firstName: value, firstNameError: "" });
				break;
			case "lastName":
				if (typeof value !== "string") {
					return;
				}
				setUserCreateData({ ...userCreateData, lastName: value, lastNameError: "" });
				break;
			case "roles":
				if (!Array.isArray(value)) {
					return;
				}
				const resolvedRoles = value.map((roleName) => {
					return availableRoles.find((availableRole) => availableRole.name === roleName) ?? { id: roleName, name: roleName };
				});
				setUserCreateData({ ...userCreateData, roles: resolvedRoles, roleError: "" });
				break;
			default:
				break;
		}
	};

	const validateUserCreateData = () => {
		let isValid = true;
		let emailError = "";
		let firstNameError = "";
		let lastNameError = "";
		let roleError = "";

		if (!userCreateData.email) {
			emailError = "Required *";
			isValid = false;
		}

		if (!userCreateData.firstName) {
			firstNameError = "Required *";
			isValid = false;
		}

		if (!userCreateData.lastName) {
			lastNameError = "Required *";
			isValid = false;
		}

		if (userCreateData.roles.length === 0) {
			roleError = "Required *";
			isValid = false;
		}

		setUserCreateData({
			...userCreateData,
			emailError: emailError,
			firstNameError: firstNameError,
			lastNameError: lastNameError,
			roleError: roleError,
		});

		return isValid;
	};

	const createUser = () => {
		if (validateUserCreateData()) {
			const userCreationObject = {
				email: userCreateData.email,
				firstName: userCreateData.firstName,
				lastName: userCreateData.lastName,
				roles: userCreateData.roles.map((role) => role.name)
			};
			axios.post(apiRoutes.createUser, userCreationObject, {
				headers: authHeader,
			}).then(() => {
				handleCloseUserCreateModal();
				setUserSeed(Math.random());
			}).catch((err: AxiosError<{ message?: string }>) => {
				console.error(err);
				const errorMessage = err.response?.data?.message ?? "";
				if (errorMessage.includes("already exists")) {
					setUserCreateData({ ...userCreateData, emailError: messages.EMAIL_ALREADY_EXISTS_ERROR_MSG });
					return;
				}
				handleToastAlert(messageLevels.ERROR, errorMessage || messages.USER_CREATION_ERROR_MSG);
			});
		}
	};

	const deleteUser = () => {
		if (!userBeingDeleted?.id) {
			return;
		}
		axios.delete(apiRoutes.deleteUser(userBeingDeleted.id), {
			headers: authHeader
		}).then(() => {
			handleCloseUserDeletionDialog();
			setUserSeed(Math.random());
		}).catch(err => {
			console.error(err);
			handleToastAlert(messageLevels.ERROR, messages.USER_DELETION_ERROR_MSG);
		});
	};

	const bulkDeleteUsers = () => {
		const selectedIds = gridRowSelectionIdsArray(selectedUsers, organizationUsers);
		const selectedLookup = new Set(selectedIds.map(String));
		const userObjs = organizationUsers
			.filter((user) => selectedLookup.has(String(user.id)))
			.map((user) => ({ id: user.id, descopeUserId: user.descopeUserId }));
		const deleteData = {
			usersToDelete: userObjs
		};
		const plural = selectedIds.length > 1;
		axios.delete(apiRoutes.bulkDeleteUsers, { headers: authHeader, data: deleteData })
			.then(() => {
				setBulkDeleteDialogOpen(false);
				setSelectedUsers(emptyGridRowSelectionModel());
				setUserSeed(Math.random());
			})
			.catch((err) => {
				console.error(err);
				setBulkDeleteDialogOpen(false);
				handleToastAlert(messageLevels.ERROR, messages.USER_DELETION_ERROR_MSG);
			});
	};

	const handleCloseUserCreateModal = () => {
		setUserCreationDialogOpen(false);
		setAvailableRoles([]);
		setUserCreateData(userCreateInitialState);
	};

	const toggleAddNewDialog = () => {
		axios.get<Role[]>(apiRoutes.getRoles, { headers: authHeader })
			.then(response => {
				setAvailableRoles(response.data);
			})
			.catch(error => {
				console.error("Error fetching roles:", error);
			});
		setUserCreationDialogOpen(!userCreationDialogOpen);
	};

	useEffect(() => {
		initUserManagement();
		handleInitializeSortModel();
	}, [userSeed]);

	const selectedUserCount = resolveGridSelectedRowIds(selectedUsers, organizationUsers).length;

	return (
		<>
			<CoAppDatagridHeader
				iconKind="users"
				searchIsPresent={true}
				{...quickFilterSearchProps}
				searchPlaceholder="Search all columns…"
				filterControl={filterControl}
				title="Users"
				resourceType="User"
				addOnClickHandler={toggleAddNewDialog}
				addIsPresent={organizationSsoStatus ? false : true}
				editIsPresent={false}
				deleteIsPresent={false}
			/>
			<CoAppStandardDataGrid
				apiRef={apiRef}
				filterButtonEl={filterButtonEl}
				filterModel={gridFilterModel}
				onFilterModelChange={onFilterModelChange}
				visibleRowCount={visibleRowCount}
				columns={columns}
				rows={organizationUsers}
				pinnedColumns={["actions"]}
				allowSelection={organizationSsoStatus ? false : true}
				targetResource="users"
				gridKey="users"
				selectionModel={selectedUsers}
				setSelectionModel={setSelectedUsers}
				loading={loading}
				sortModel={sortModel}
				handleSortModelChange={handleSortModelChange}
				selectionFooter={
					<SettingsDataGridSelectionRow
						resourceNoun="user"
						visibleCount={visibleRowCount}
						selectedCount={selectedUserCount}
						loading={loading}
						onClearSelection={() => setSelectedUsers(emptyGridRowSelectionModel())}
						actions={organizationSsoStatus ? undefined : [{
							label: "Delete",
							icon: <Delete sx={destructiveIconSx} />,
							onClick: () => setBulkDeleteDialogOpen(true),
							variant: "destructive",
						}]}
					/>
				}
			/>
			<CoAppDestructiveConfirmationModal
				dialogOpen={userDeletionDialogOpen}
				dialogTitle={`Delete ${userBeingDeleted.email ?? ""}?`}
				dialogMessage={messages.DELETION_CONFIRMATION_MSG(userBeingDeleted.email ?? "", "user")}
				confirmClickHandler={deleteUser}
				cancelClickHandler={handleCloseUserDeletionDialog}
				actionText="Delete"
			/>
			<CoAppDestructiveConfirmationModal
				dialogOpen={bulkDeleteDialogOpen}
				dialogTitle={`Delete selected ${gridRowSelectionSize(selectedUsers, organizationUsers) > 1 ? "users" : "user"}?`}
				dialogMessage={"Are you sure you want to bulk delete all the selected users? This action cannot be undone."}
				confirmClickHandler={bulkDeleteUsers}
				cancelClickHandler={() => setBulkDeleteDialogOpen(false)}
				actionText="Delete"
			/>
			<CoAppCreateUserModal
				dialogOpen={userCreationDialogOpen}
				availableRoles={availableRoles}
				cancelClickHandler={handleCloseUserCreateModal}
				confirmClickhandler={createUser}
				userDataChangeHandler={handleUserCreateDataChange}
				userData={userCreateData}
			/>
		</>
	);
}
